import { Component, ChangeDetectorRef, OnInit, signal } from '@angular/core';
import { LoadingComponent } from '@/components/loading/loading.component';
import { FormControl, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ApiService } from '@/apiConnection/ApiService';
import { Urls } from '@/apiConnection/urls';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { Utils } from '@/utils/utils';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';

@Component({
  imports: [
    LoadingComponent,
    DecimalInputDirective,
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    CustomInputComponent,
    CustomSelectComponent,
  ],
  templateUrl: './inventory-history-form.page.html'
})
export class InventoryHistoryFormPage implements OnInit {

  public loading = signal(false);
  public saving = signal(false);
  public viewOnly = false;
  public saveForm!: FormGroup;
  public search = new FormControl('');

  public dropdowns: any = {
    offices: [],
    products: [],
  };

  id: number | string = 0;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.id = params['id'] || 0;
      this.viewOnly = this.router.url.includes('/inventory-history/view');

      this.createForm();

      if (this.id != 0) {
        this.getById(this.id);
      } else {
        this.getDropdowns();
      }
    });
  }

  createForm() {
    this.saveForm = new FormGroup({
      date: new FormControl(new Date().toISOString().split('T')[0], [Validators.required]),
      office_id: new FormControl(null),
      details: new FormArray([]),
    });
  }

  get details() { return this.saveForm.get('details') as FormArray; }
  get detailControls() { return this.details.controls; }

  getDropdowns() {
    const config = { offices: {}, products: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        this.dropdowns.offices = data.offices || [];
        this.dropdowns.products = data.products || [];

        this.saveForm.get('office_id')?.setValue(this.dropdowns.offices[0]?.id ?? null);

        this.cdr.detectChanges();
      }
    });
  }

  existingProductIds(): number[] {
    return this.details.controls.map(c => c.get('product_id')?.value);
  }

  filteredProducts(): any[] {
    const term = (this.search.value || '').toLowerCase().trim();
    if (!term) return [];

    const existingIds = this.existingProductIds();

    return this.dropdowns.products
      .filter((p: any) => !existingIds.includes(p.id) && (
        p.name.toLowerCase().includes(term) ||
        (p.code && p.code.toLowerCase().includes(term))
      ))
      .slice(0, 8);
  }

  loadAllProducts() {
    const existingIds = this.existingProductIds();

    this.dropdowns.products
      .filter((p: any) => !existingIds.includes(p.id))
      .forEach((p: any) => this.addRow(p));
  }

  selectProductFromSearch(product: any) {
    this.addRow(product);
    this.search.setValue('');
  }

  addRow(product: any) {
    this.details.push(new FormGroup({
      product_id: new FormControl(product.id, [Validators.required]),
      code: new FormControl(product.code),
      name: new FormControl(product.name),
      prev_stock: new FormControl(Number(product.stock) || 0),
      new_stock: new FormControl(Number(product.stock) || 0, [Validators.required, Validators.min(0)]),
    }));
  }

  removeRow(index: number) {
    this.details.removeAt(index);
  }

  difference(group: any): number {
    const prev = Number(group.get('prev_stock')?.value) || 0;
    const next = Number(group.get('new_stock')?.value) || 0;
    return next - prev;
  }

  getById(id: number | string) {
    this.loading.set(true);
    this.apiService.getById(Urls.urls.historyInventory, id).subscribe({
      next: (data: any) => {
        this.saveForm.patchValue({
          date: data.date ? data.date.split(' ')[0] : null,
          office_id: data.office_id ?? null,
        });

        this.dropdowns.offices = data.office ? [data.office] : [];

        (data.details || []).forEach((d: any) => {
          this.details.push(new FormGroup({
            product_id: new FormControl(d.product_id),
            code: new FormControl(d.product?.code ?? ''),
            name: new FormControl(d.product?.name ?? ''),
            prev_stock: new FormControl(Number(d.prev_stock)),
            new_stock: new FormControl(Number(d.new_stock)),
          }));
        });

        if (this.viewOnly) {
          this.saveForm.disable();
        }

        this.loading.set(false);
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  saveEntity() {
    if (this.saveForm.invalid || this.details.length === 0) return;

    this.saving.set(true);

    const value = this.saveForm.getRawValue();
    const payload = {
      date: value.date,
      office_id: value.office_id,
      details: value.details.map((d: any) => ({ product_id: d.product_id, new_stock: d.new_stock })),
    };

    this.apiService.save(Urls.urls.historyInventory, payload).subscribe({
      next: () => {
        Utils.notification.success('Recuento de inventario guardado correctamente');
        this.router.navigate(['/inventory-history']);
      },
      error: (error: any) => {
        this.saving.set(false);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al guardar el recuento de inventario');
        this.cdr.detectChanges();
      },
      complete: () => {
        this.saving.set(false);
      },
    });
  }
}
